import { create } from 'zustand';
import { diagnosticLog } from '../utils/diagnosticLogger';

export type Operation = {
  id: string;
  label: string;
  startedAt: number;
  progress?: number;
  totalSteps?: number;
  currentStep?: number;
  phase?: string | null;
  currentLabel?: string | null;
  etaMs?: number | null;
  elapsedMs?: number | null;
  filesPerSecond?: number | null;
  errorsCount?: number | null;
  /** Folder path key for scan timing baselines (local library). */
  baselineKey?: string | null;
  /** Wall-clock ms when the current `phase` began (for per-step elapsed/countdown). */
  phaseStartedAt?: number | null;
  /** NAS walk entry/file count — separate from folder-index `currentStep` during multi-source scans. */
  walkFileCount?: number | null;
  /** v11.0.116 honest scan metrics */
  walkFilesFound?: number | null;
  terminalFiles?: number | null;
  pendingJobs?: number | null;
  scanSetAsideCount?: number | null;
  scanIndexedCount?: number | null;
  scanUnchangedCount?: number | null;
  /** Truthful status line e.g. "Scanning — 67,200 of 68,840 · reading tags" */
  scanStatusLine?: string | null;
  /** Aggregated NAS walk file count across folders. */
  walkRunningTotal?: number | null;
  /** Monotonic albums_identified peak during scan — never decreases within a run. */
  scanAlbumsIdentifiedPeak?: number | null;
  /** Aggregate phase for v153 interleaved multi-source scans (stable label/ETA). */
  scanAggregatePhase?: 'walking' | 'reading_tags' | 'finishing' | 'idle' | null;
  scanSourcesCompleted?: number | null;
  scanSourcesTotal?: number | null;
  scanInterleavedMultiSource?: boolean;
  /** Smoothed global ETA in seconds — avoids per-event pipeline re-sum spikes. */
  globalEtaSec?: number | null;
  /** Connector emits cumulative walk/terminal (overlap era) — use direct, do not accumulate. */
  scanGlobalDirectTotals?: boolean;
  /** Walk still discovering files — percent/ETA use provisional totals. */
  scanWalkStillRunning?: boolean;
  /** Suppress misleading low ETA while global walk total may still grow. */
  globalEtaEstimating?: boolean;
  /** Connector finalize_stage during phase=finalize (#422). */
  scanFinalizeStage?: string | null;
  /** Post-scan background cover enrichment op — keeps strip visible through done dismiss. */
  coverEnrichment?: boolean;
  artworkFilled?: number | null;
  artworkExhausted?: number | null;
};

type OperationPatch = Partial<Pick<
  Operation,
  'label' | 'progress' | 'totalSteps' | 'currentStep' |
  'phase' | 'currentLabel' | 'etaMs' | 'elapsedMs' | 'filesPerSecond' | 'errorsCount' |
  'baselineKey' | 'phaseStartedAt' | 'walkFileCount' |
  'walkFilesFound' | 'terminalFiles' | 'pendingJobs' |
  'scanSetAsideCount' | 'scanIndexedCount' | 'scanUnchangedCount' | 'scanStatusLine' |
  'walkRunningTotal' | 'scanAlbumsIdentifiedPeak' |
  'scanAggregatePhase' | 'scanSourcesCompleted' | 'scanSourcesTotal' |
  'scanInterleavedMultiSource' | 'globalEtaSec' | 'scanGlobalDirectTotals' |
  'scanWalkStillRunning' | 'globalEtaEstimating' | 'scanFinalizeStage' |
  'coverEnrichment' | 'artworkFilled' | 'artworkExhausted'
>>;

type OperationProgressState = {
  operations: Record<string, Operation>;
  beginOperation: (label: string, initial?: OperationPatch) => string;
  updateOperation: (id: string, patch: OperationPatch) => void;
  updateOperationProgress: (id: string, fields: {
    current?: number | null;
    total?: number | null;
    phase?: string | null;
    currentLabel?: string | null;
    etaMs?: number | null;
    filesPerSecond?: number | null;
    errorsCount?: number | null;
  }) => void;
  endOperation: (id: string) => void;
  getActive: () => Operation[];
};

function clampProgress(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.max(0, Math.min(1, value));
}

function makeOpId(): string {
  return `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function operationPatchIsNoop(existing: Operation, patch: OperationPatch): boolean {
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if ((existing as Record<string, unknown>)[key] !== value) return false;
  }
  return true;
}

let loggedScanProgressNoopGuard = false;
let loggedScanProgressFieldsNoopGuard = false;

export const useOperationProgressStore = create<OperationProgressState>((set, get) => ({
  operations: {},

  beginOperation: (label, initial) => {
    const id = makeOpId();
    const op: Operation = {
      id,
      label: label || 'Working...',
      startedAt: Date.now(),
      progress: clampProgress(initial?.progress),
      totalSteps: initial?.totalSteps,
      currentStep: initial?.currentStep,
      phase: initial?.phase,
      baselineKey: initial?.baselineKey,
      phaseStartedAt: initial?.phaseStartedAt ?? Date.now(),
    };
    set((state) => ({
      operations: {
        ...state.operations,
        [id]: op,
      },
    }));
    return id;
  },

  updateOperation: (id, patch) => {
    if (!id) return;
    set((state) => {
      const existing = state.operations[id];
      if (!existing) return state;
      if (operationPatchIsNoop(existing, patch)) {
        if (!loggedScanProgressNoopGuard) {
          loggedScanProgressNoopGuard = true;
          diagnosticLog('info', 'react-185', '[scan-progress] loop-break: skipped redundant updateOperation');
        }
        return state;
      }
      return {
        operations: {
          ...state.operations,
          [id]: {
            ...existing,
            ...patch,
            progress: clampProgress(patch.progress ?? existing.progress),
          },
        },
      };
    });
  },

  updateOperationProgress: (id, fields) => {
    if (!id) return;
    set((state) => {
      const existing = state.operations[id];
      if (!existing) return state;
      const { current, total, phase, currentLabel, etaMs, filesPerSecond, errorsCount } = fields;
      const progress = (typeof current === 'number' && typeof total === 'number' && total > 0)
        ? clampProgress(current / total)
        : existing.progress;
      const next: Operation = {
        ...existing,
        progress,
        currentStep: typeof current === 'number' ? current : existing.currentStep,
        totalSteps: typeof total === 'number' ? total : existing.totalSteps,
        phase: phase !== undefined ? phase : existing.phase,
        currentLabel: currentLabel !== undefined ? currentLabel : existing.currentLabel,
        etaMs: etaMs !== undefined ? etaMs : existing.etaMs,
        filesPerSecond: filesPerSecond !== undefined ? filesPerSecond : existing.filesPerSecond,
        errorsCount: errorsCount !== undefined ? errorsCount : existing.errorsCount,
      };
      if (
        next.progress === existing.progress
        && next.currentStep === existing.currentStep
        && next.totalSteps === existing.totalSteps
        && next.phase === existing.phase
        && next.currentLabel === existing.currentLabel
        && next.etaMs === existing.etaMs
        && next.filesPerSecond === existing.filesPerSecond
        && next.errorsCount === existing.errorsCount
      ) {
        if (!loggedScanProgressFieldsNoopGuard) {
          loggedScanProgressFieldsNoopGuard = true;
          diagnosticLog('info', 'react-185', '[scan-progress] loop-break: skipped redundant updateOperationProgress');
        }
        return state;
      }
      return {
        operations: {
          ...state.operations,
          [id]: next,
        },
      };
    });
  },

  endOperation: (id) => {
    if (!id) return;
    set((state) => {
      if (!state.operations[id]) return state;
      const next = { ...state.operations };
      delete next[id];
      return { operations: next };
    });
  },

  getActive: () =>
    Object.values(get().operations).sort((a, b) => a.startedAt - b.startedAt),
}));
